/**
 * The map's view, as the address bar holds it: which area, who is chosen,
 * how far back their road goes, or which visit's road is shown.
 */
import { instantIn, localIn, MARKET_TIME_ZONE } from "@/shared/lib/format";
import { idOf } from "@/shared/lib/search";

import { DEFAULT_SPAN, SPANS, type OwnSpan, type RoadSpan, type Span } from "../api/types";

export interface MapSearch {
  /** A service area's id. */
  area?: string;
  /** The specialist chosen: their road is drawn. */
  specialist?: string;
  /** How many hours back their road goes; the service's own two when left out. */
  hours?: Span;
  /**
   * Their road through a span of one's own choosing instead: from this
   * moment, by the market's clock (`2026-10-03T08:00`)...
   */
  from?: string;
  /** ...to this one; up to now when left out. */
  to?: string;
  /** A visit: its road is drawn in place of a specialist's. */
  visit?: string;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseMapSearch(raw: Readonly<Record<string, unknown>>): MapSearch {
  const search: MapSearch = {};
  const area = idOf(raw["area"]);
  if (area !== undefined) search.area = area;
  const visit = idOf(raw["visit"]);
  // One road at a time: a visit's, or a specialist's.
  if (visit !== undefined) {
    search.visit = visit;
    return search;
  }
  const specialist = idOf(raw["specialist"]);
  if (specialist !== undefined) {
    search.specialist = specialist;
    const from = momentOf(raw["from"]);
    // One span at a time: a span of one's own, or the last hours.
    if (from !== undefined) {
      search.from = from;
      const to = momentOf(raw["to"]);
      if (to !== undefined && to > from) search.to = to;
      return search;
    }
    const hours = SPANS.find((known) => known === Number(raw["hours"]));
    if (hours !== undefined && hours !== DEFAULT_SPAN) search.hours = hours;
  }
  return search;
}

/** A moment as a `datetime-local` field writes it; anything else is not one. */
function momentOf(value: unknown): string | undefined {
  return typeof value === "string" && instantIn(value, MARKET_TIME_ZONE) !== null
    ? value
    : undefined;
}

/** The hours the road goes back, when it is of the last hours. */
export function spanOf(search: MapSearch): Span {
  return search.hours ?? DEFAULT_SPAN;
}

/** The span of one's own the road is asked for, or null when it is of the last hours. */
export function ownSpanOf(search: MapSearch): OwnSpan | null {
  if (search.from === undefined) return null;
  return search.to === undefined ? { from: search.from } : { from: search.from, to: search.to };
}

/** What the road is asked for: a span of one's own when there is one, else the last hours. */
export function roadSpanOf(search: MapSearch): RoadSpan {
  return ownSpanOf(search) ?? spanOf(search);
}

/**
 * The view with the road through a span of one's own. Without one it starts
 * from the day so far by the market's clock: midnight to now.
 */
export function withOwnSpan(
  search: MapSearch,
  own: { from?: string; to?: string } = {},
  now: Date = new Date(),
): MapSearch {
  const { hours: _, from: __, to: ___, ...rest } = search;
  const until = localIn(now, MARKET_TIME_ZONE);
  const from = momentOf(own.from) ?? `${until.slice(0, 10)}T00:00`;
  const to = momentOf(own.to);
  return to === undefined ? { ...rest, from } : { ...rest, from, to };
}

/** The view with this specialist chosen, or with nobody: the area stays. */
export function withSpecialist(search: MapSearch, userId: string | null): MapSearch {
  const next: MapSearch = {};
  if (search.area !== undefined) next.area = search.area;
  if (userId !== null) next.specialist = userId;
  return next;
}

/** The view with the road of the last hours: a span of one's own is dropped. */
export function withSpan(search: MapSearch, hours: number): MapSearch {
  const { hours: _, from: __, to: ___, ...rest } = search;
  const known = SPANS.find((each) => each === hours);
  return known === undefined || known === DEFAULT_SPAN ? rest : { ...rest, hours: known };
}

/** The view of another area: nobody chosen, for whoever was may not work there. */
export function withArea(area: string | undefined): MapSearch {
  return area === undefined ? {} : { area };
}

/** The view with a visit's road in place of whatever was chosen. */
export function withVisit(search: MapSearch, visitId: string): MapSearch {
  return search.area === undefined ? { visit: visitId } : { area: search.area, visit: visitId };
}
